import {
    type DynamicWidgetIssue,
    type DynamicWidgetModel,
} from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_schema';

/*
 * Bounded diagnostic the "Ask Phoebe" control attaches to a normal user
 * message. It is the generated request type, so the browser cannot invent a
 * field the server does not accept, and every limit here mirrors
 * `agent_event_framework/widget_diagnostics.py`. The client sends at most the
 * latest diagnostic per widget; there is no resolution machinery.
 */

type DynamicWidgetDiagnosticIssueCode =
    | 'source_invalid'
    | 'source_missing'
    | 'source_stale'
    | 'permission_denied'
    | 'action_rejected'
    | 'action_failed'
    | 'live_refresh_failed'
    | 'render_failed'
    | 'unsupported';

// Closed browser contract. The implementation layer proves parity with the
// generated request type and the server validates every field and size bound.
export type DynamicWidgetDiagnosticRequest = {
    readonly schema_version: 1;
    readonly widget_id: string;
    readonly template: string;
    readonly template_version: number;
    readonly issue_codes: Array<DynamicWidgetDiagnosticIssueCode>;
    readonly failed_binding_ids: Array<string>;
    readonly source_event_ids: Array<string>;
    readonly source_call_ids: Array<string>;
    readonly source_tool_names: Array<string>;
    readonly action_id: string | null;
    readonly action_state: string | null;
    readonly captured_at: string;
    readonly last_valid_at: string | null;
};

export const ASK_PHOEBE_DIAGNOSTIC_MESSAGE =
    'Help me understand and fix this card';

export const DYNAMIC_WIDGET_DIAGNOSTIC_MAX_LIST_ITEMS = 20;
export const DYNAMIC_WIDGET_DIAGNOSTIC_MAX_SERIALIZED_BYTES = 8 * 1024;

const MAX_WIDGET_ID_CHARS = 255;
const MAX_TEMPLATE_CHARS = 64;
const MAX_BINDING_ID_CHARS = 255;
const MAX_SOURCE_EVENT_ID_CHARS = 255;
const MAX_SOURCE_CALL_ID_CHARS = 512;
const MAX_SOURCE_TOOL_NAME_CHARS = 255;
const MAX_ACTION_STATE_CHARS = 64;

const UUID_PATTERN =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Keeps only IDs the server will accept and caps the list. An over-long ID is
 * dropped rather than truncated: a truncated ID resolves to nothing and would
 * point the agent at the wrong binding.
 */
function boundedIds(values: Iterable<string>, maxChars: number): Array<string> {
    const bounded: Array<string> = [];
    for (const value of new Set(values)) {
        if (value.length === 0 || value.length > maxChars) {
            continue;
        }
        bounded.push(value);
        if (bounded.length === DYNAMIC_WIDGET_DIAGNOSTIC_MAX_LIST_ITEMS) {
            break;
        }
    }
    return bounded;
}

function readSafeDetailString(
    issues: ReadonlyArray<DynamicWidgetIssue>,
    key: string,
): string | null {
    for (const issue of issues) {
        const value = issue.safeDetails[key];
        if (typeof value === 'string' && value.length > 0) {
            return value;
        }
    }
    return null;
}

/**
 * Newest timestamp the widget can prove: an event capture time or a resource
 * fetch time. The issue catalog's `lastValidAt` detail is a display label
 * (`3:04 PM PDT`), not a timestamp, so it is deliberately not used here.
 */
function latestSourceTimestamp(model: DynamicWidgetModel): string | null {
    let latest: string | null = null;
    for (const source of model.sources) {
        const candidate =
            source.kind === 'event_source'
                ? source.capturedAt
                : source.fetchedAt;
        if (candidate === null || Number.isNaN(Date.parse(candidate))) {
            continue;
        }
        if (latest === null || Date.parse(candidate) > Date.parse(latest)) {
            latest = candidate;
        }
    }
    return latest;
}

function serializedByteLength(
    diagnostic: DynamicWidgetDiagnosticRequest,
): number {
    return new TextEncoder().encode(JSON.stringify(diagnostic)).length;
}

/**
 * Builds the diagnostic for one widget. `issue` is the issue whose "Ask
 * Phoebe" control was used; it leads the code list and is merged with the
 * model's own issues, because a runtime issue (a render failure, a rejected
 * action) never lives in the committed model.
 */
export function buildDynamicWidgetDiagnostic({
    model,
    issue,
    capturedAt = new Date().toISOString(),
}: {
    readonly model: DynamicWidgetModel;
    readonly issue?: DynamicWidgetIssue;
    readonly capturedAt?: string;
}): DynamicWidgetDiagnosticRequest {
    const issues =
        issue === undefined
            ? model.issues
            : [
                  issue,
                  ...model.issues.filter(
                      (candidate) => candidate.id !== issue.id,
                  ),
              ];
    const eventSources = model.sources.filter(
        (source) => source.kind === 'event_source',
    );
    const actionId = readSafeDetailString(issues, 'actionId');
    const actionState = readSafeDetailString(issues, 'actionStatus');

    let diagnostic: DynamicWidgetDiagnosticRequest = {
        schema_version: 1,
        widget_id: model.widgetId.slice(0, MAX_WIDGET_ID_CHARS),
        template: model.template.slice(0, MAX_TEMPLATE_CHARS),
        template_version: model.templateVersion,
        issue_codes: [
            ...new Set(issues.map((candidate) => candidate.code)),
        ].slice(0, DYNAMIC_WIDGET_DIAGNOSTIC_MAX_LIST_ITEMS),
        failed_binding_ids: boundedIds(
            issues.flatMap((candidate) => candidate.bindingIds),
            MAX_BINDING_ID_CHARS,
        ),
        source_event_ids: boundedIds(
            eventSources.flatMap((source) =>
                source.toolOutputEventId === null
                    ? [source.toolCallEventId]
                    : [source.toolCallEventId, source.toolOutputEventId],
            ),
            MAX_SOURCE_EVENT_ID_CHARS,
        ),
        source_call_ids: boundedIds(
            eventSources.map((source) => source.callId),
            MAX_SOURCE_CALL_ID_CHARS,
        ),
        source_tool_names: boundedIds(
            eventSources.map((source) => source.toolName),
            MAX_SOURCE_TOOL_NAME_CHARS,
        ),
        action_id:
            actionId !== null && UUID_PATTERN.test(actionId) ? actionId : null,
        action_state:
            actionState === null
                ? null
                : actionState.slice(0, MAX_ACTION_STATE_CHARS),
        captured_at: capturedAt,
        last_valid_at: latestSourceTimestamp(model),
    };

    // The server rejects anything over 8 KB. Shrink the ID lists rather than
    // let the send fail; the codes and the widget identity matter most.
    const shrinkableKeys = [
        'failed_binding_ids',
        'source_event_ids',
        'source_call_ids',
        'source_tool_names',
    ] as const;
    for (const key of shrinkableKeys) {
        while (
            serializedByteLength(diagnostic) >
                DYNAMIC_WIDGET_DIAGNOSTIC_MAX_SERIALIZED_BYTES &&
            diagnostic[key].length > 0
        ) {
            diagnostic = {
                ...diagnostic,
                [key]: diagnostic[key].slice(0, diagnostic[key].length - 1),
            };
        }
    }
    return diagnostic;
}
